import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowUpRight, Loader2 } from 'lucide-react';
import { Slider } from '@/components/ui/slider';
import { Reveal, SectionHeading } from './Reveal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ['Vacant right now', 'Currently let (AST)', 'With a letting agent', 'Self-managed', 'New acquisition'];

const INITIAL = { name: '', email: '', phone: '', postcode: '', bedrooms: 3, rental_status: STATUSES[0], message: '' };

const fieldCls =
  'w-full rounded-xl border border-[#DCBC7F]/20 bg-[#155533] px-5 py-3.5 text-sm font-light text-[#FCFBFA] placeholder:text-[#8DAF9C]/60 outline-none transition-colors duration-300 focus:border-[#DCBC7F]/60';

export default function Enquiry() {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const low = form.bedrooms * 560;
  const high = form.bedrooms * 680;
  const fmt = (n) => `£${n.toLocaleString('en-GB')}`;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success('Enquiry received — our team will respond within one working day.', {
        description: `Reference for ${form.postcode.toUpperCase()} logged.`,
      });
      setForm(INITIAL);
    } catch (err) {
      toast.error('Something went wrong sending your enquiry.', {
        description: 'Please try again, or email info@thegatestays.co.uk directly.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="enquiry" className="relative overflow-hidden bg-[#1B6640] py-28 lg:py-40" data-testid="enquiry-section">
      <div
        className="pointer-events-none absolute -left-40 bottom-0 h-[480px] w-[480px] rounded-full opacity-15"
        style={{ background: 'radial-gradient(circle, #DCBC7F 0%, transparent 65%)', filter: 'blur(90px)' }}
      />
      <div className="mx-auto grid max-w-7xl gap-16 px-6 md:px-12 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <SectionHeading label="Contact Us Today" title="Turn your property into a high-earning, hassle-free asset" />
          <Reveal delay={0.15}>
            <p className="mt-6 text-sm font-light leading-relaxed text-[#C4D4C9] sm:text-base">
              Guaranteed rent and complete peace of mind. Tell us about your property and we'll come back with an
              offer within 24 hours — contracts can be signed within 48.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 font-display text-lg italic text-[#DCBC7F]">
              Professional, secure and stress-free property management — that's our promise.
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <div className="mt-12 rounded-2xl border border-[#DCBC7F]/25 bg-[#155533] p-8 sm:p-10" data-testid="rent-calculator">
              <p className="mono-label text-[#DCBC7F]">Instant estimate</p>
              <div className="mt-8 flex items-end justify-between">
                <p className="text-sm font-light text-[#C4D4C9]">Bedrooms</p>
                <p className="font-display text-3xl font-semibold text-[#F3E9D7]" data-testid="rent-calculator-bedrooms">
                  {form.bedrooms}
                </p>
              </div>
              <div className="mt-6" data-testid="rent-calculator-slider">
                <Slider
                  min={1}
                  max={6}
                  step={1}
                  value={[form.bedrooms]}
                  onValueChange={([v]) => setForm((f) => ({ ...f, bedrooms: v }))}
                  aria-label="Number of bedrooms"
                />
              </div>
              <div className="mt-8 border-t border-[#DCBC7F]/15 pt-6">
                <p className="text-xs font-light text-[#8DAF9C]">Estimated guaranteed rent</p>
                <p className="font-display mt-2 text-3xl font-semibold text-[#DCBC7F] sm:text-4xl" data-testid="rent-calculator-estimate">
                  {fmt(low)} – {fmt(high)}
                  <span className="ml-2 text-base font-normal text-[#8DAF9C]">/ month</span>
                </p>
                <p className="mt-3 text-xs font-light leading-relaxed text-[#8DAF9C]">
                  Indicative range only. Your formal proposal depends on location, condition and layout.
                </p>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.2} className="lg:col-span-7">
          <form
            onSubmit={submit}
            data-testid="landlord-enquiry-form"
            className="rounded-2xl border border-[#DCBC7F]/25 bg-[#207347] p-8 sm:p-12"
          >
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <input
                data-testid="landlord-form-name"
                required
                value={form.name}
                onChange={set('name')}
                placeholder="Full name"
                className={fieldCls}
              />
              <input
                data-testid="landlord-form-email"
                required
                type="email"
                value={form.email}
                onChange={set('email')}
                placeholder="Email address"
                className={fieldCls}
              />
              <input
                data-testid="landlord-form-phone"
                required
                type="tel"
                value={form.phone}
                onChange={set('phone')}
                placeholder="Phone number"
                className={fieldCls}
              />
              <input
                data-testid="landlord-form-postcode"
                required
                value={form.postcode}
                onChange={set('postcode')}
                placeholder="Property postcode"
                className={fieldCls}
              />
              <select
                data-testid="landlord-form-status"
                value={form.rental_status}
                onChange={set('rental_status')}
                className={`${fieldCls} appearance-none sm:col-span-2`}
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s} className="bg-[#155533]">
                    {s}
                  </option>
                ))}
              </select>
              <textarea
                data-testid="landlord-form-message"
                value={form.message}
                onChange={set('message')}
                placeholder="Anything else we should know? (optional)"
                rows={4}
                className={`${fieldCls} resize-none sm:col-span-2`}
              />
            </div>
            <button
              data-testid="landlord-form-submit-button"
              type="submit"
              disabled={submitting}
              className="group mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-[#DCBC7F] px-7 py-4 text-sm font-semibold text-[#155533] transition-colors duration-300 hover:bg-[#E9CD96] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Sending…
                </>
              ) : (
                <>
                  Submit Enquiry for Free Rent Assessment
                  <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </>
              )}
            </button>
            <p className="mt-5 text-center text-xs font-light text-[#8DAF9C]">
              No fees. No obligation. Response within one working day.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
